/**
 * @fileoverview Service for integrating with HubSpot for lead capture
 * This service handles sending data to HubSpot and temporarily storing assessment results
 * until users can be onboarded into the full system.
 */

import { deiterateAPI } from '../js/external-js/variableModules/utils/deiterateAPI.util';
import { CURRENT_E8_VERSION } from '../app/pages/essential-eight/constants/versions';
import { PRIVACY_ACT_QUESTIONNAIRE } from '../app/pages/privacy-act/constants/questions';
import { generateAssessmentPdf, generatePdf, pdfToBase64 } from '../app/pages/essential-eight/components/pdf-utils';

/**
 * Utility function to force light mode for PDF generation (works outside React context)
 */
const forceLightModeForPdf = async () => {
  const documentElement = document.documentElement;
  const wasDark = documentElement.classList.contains('dark');
  
  // Force light mode
  documentElement.classList.remove('dark');
  
  // Wait for theme to apply
  await new Promise(resolve => setTimeout(resolve, 100));
  
  return wasDark;
};

/**
 * Restore previous theme mode after PDF generation
 */
const restoreThemeAfterPdf = (wasDark: boolean) => {
  if (wasDark) {
    document.documentElement.classList.add('dark');
  }
};

/**
 * Questionnaire types for HubSpot integration
 */
export const QuestionnaireType = {
  ESSENTIAL_EIGHT: 'ESSENTIAL_EIGHT' as const,
  PRIVACY_ACT: 'PRIVACY_ACT' as const
} as const;

export type QuestionnaireType = typeof QuestionnaireType[keyof typeof QuestionnaireType];

/**
 * Lead data interface for HubSpot submission
 */
export interface LeadData {
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  phone?: string;
  jobTitle?: string;
  marketingConsent?: boolean;
}

/**
 * Get the user's browser timezone
 */
const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
};

/**
 * Get the user's browser language
 */
const getUserLanguage = (): string => {
  return navigator.language || 'en';
};

/**
 * Submit lead data to HubSpot using the internal API endpoint
 * 
 * @param {LeadData} data - Lead information to submit to HubSpot
 * @returns {Promise<boolean>} Success status of the submission
 */
export const submitLeadToHubspot = async (data: LeadData, questionnaireType: QuestionnaireType = 'ESSENTIAL_EIGHT'): Promise<boolean> => {
  try {
    console.log('[HubspotService] Attempting to submit lead data for:', data.email);
    
    // Using the project's standardized API utility which handles environment detection
    // and proper request formatting automatically
    const responseData = await deiterateAPI.POST('/hubspot-token', {
      body: JSON.stringify({
        email: data.email,
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        timezone: getUserTimezone(),
        language: getUserLanguage(),
        // Marketing consent and subscriptions
        marketingConsent: data.marketingConsent === true,
        consentSource: questionnaireType === 'PRIVACY_ACT' ? "Agreed via Privacy Act Questionnaire" : "Agreed via E8 Questionnaire",
        legalBasis: "Freely given consent from contact",
        subscriptions: data.marketingConsent === true ? [
          "Marketing Information",
          "Customer Service Communication",
          "One on One"
        ] : [],
        // Additional data for our records
        metadata: {
          source: questionnaireType === 'PRIVACY_ACT' ? 'Privacy Act Assessment' : 'Essential Eight Assessment',
          company: data.company,
          jobTitle: data.jobTitle,
          phone: data.phone
        }
      }),
    }, false); // false = don't require auth for public endpoint
    
    console.log('[HubspotService] Response data:', responseData);
    
    // Store email in sessionStorage to identify this lead
    console.log('[HubspotService] Storing lead email:', data.email);
    sessionStorage.setItem(`${questionnaireType.toLowerCase()}-lead-email`, data.email);

    // Store lead company
    console.log('[HubspotService] Storing lead company:', data.company);
    sessionStorage.setItem(`${questionnaireType.toLowerCase()}-lead-company`, data.company || '');
    
    // Store the token if it was returned
    if (responseData && responseData.token) {
      sessionStorage.setItem('hubspot-token', responseData.token);
      console.log('[HubspotService] Successfully stored HubSpot token');
    }
    
    return true;
  } catch (error: any) {
    // More detailed error logging
    console.error('[HubspotService] Error submitting lead:', error.message);
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('[HubspotService] Response data:', error.response.data);
      console.error('[HubspotService] Response status:', error.response.status);
      console.error('[HubspotService] Response headers:', error.response.headers);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('[HubspotService] Request made but no response:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('[HubspotService] Error details:', error.message);
    }
    return false;
  }
};

/**
 * Store assessment data temporarily in sessionStorage
 * 
 * @param {any} assessmentData - Assessment data to store
 * @param {string} email - Email to associate with the assessment data
 */
export const storeAssessmentData = (assessmentData: any, email?: string, questionnaireType: QuestionnaireType = 'ESSENTIAL_EIGHT'): void => {
  const leadEmail = email || sessionStorage.getItem(`${questionnaireType.toLowerCase()}-lead-email`);
  
  if (!leadEmail) {
    console.error('[HubspotService] Cannot store assessment data: no email found');
    return;
  }
  
  try {
    // Ensure version information is included with the assessment data
    const versionInfo = assessmentData.questionnaireVersion || 
      (questionnaireType === 'PRIVACY_ACT' ? PRIVACY_ACT_QUESTIONNAIRE.version : CURRENT_E8_VERSION);

    // Store assessment with email reference and version info
    const dataToStore = {
      assessmentData,
      leadEmail,
      timestamp: new Date().toISOString(),
      questionnaireVersion: versionInfo
    };
    
    sessionStorage.setItem(`${questionnaireType.toLowerCase()}-assessment-data`, JSON.stringify(dataToStore));
    
    // Send to HubSpot as a custom event or property update
    // We can use the existing HubSpot API endpoint
    sendAssessmentResultsToHubspot(assessmentData, leadEmail, questionnaireType);
    
  } catch (error) {
    console.error('[HubspotService] Error storing assessment data:', error);
  }
};

/**
 * Send assessment results to HubSpot as notes with attachments
 * 
 * @param {any} assessmentData - Assessment data to send
 * @param {string} email - Email to associate with the assessment data
 */
const sendAssessmentResultsToHubspot = async (assessmentData: any, email: string, questionnaireType: QuestionnaireType = 'ESSENTIAL_EIGHT'): Promise<void> => {
  try {
    // Calculate overall maturity level for summary
    const maturityLevels = Object.values(assessmentData.maturityLevels || {});
    const overallMaturity = maturityLevels.length > 0 
      ? Math.min(...maturityLevels as number[]) 
      : 0;
    
    // Get version information, using the appropriate version as fallback
    const versionInfo = assessmentData.questionnaireVersion || 
      (questionnaireType === 'PRIVACY_ACT' ? PRIVACY_ACT_QUESTIONNAIRE.version : CURRENT_E8_VERSION);
    
    // Create summary for the notes
    const assessmentSummary = {
      overallMaturity: overallMaturity.toString(),
      completionDate: new Date().toISOString(),
      questionnaireVersion: versionInfo.version || "1.0",
      categoriesCount: Object.keys(assessmentData.maturityLevels || {}).length.toString(),
    };
    
    // Format assessment data as JSON for the note content
    const jsonResults = JSON.stringify({
      maturityLevels: assessmentData.maturityLevels || {},
      overallMaturity,
      categoryCount: Object.keys(assessmentData.maturityLevels || {}).length,
      completedAt: assessmentData.completedAt,
      questionnaireVersion: versionInfo,
      timestamp: new Date().toISOString()
    }, null, 2); // Pretty print with 2 spaces
    
    // Store in sessionStorage as backup
    sessionStorage.setItem(`${questionnaireType.toLowerCase()}-assessment-summary`, JSON.stringify({
      email,
      assessmentSummary,
      jsonResults,
      timestamp: new Date().toISOString()
    }));
    
    console.log('[HubspotService] Assessment results prepared for HubSpot with version:', versionInfo.version);
    
    // Generate PDF for HubSpot attachment
    let pdfBase64: string | null = null;
    let wasDark = false;
    
    try {
      console.log('[HubspotService] Generating PDF for HubSpot attachment');
      
      // Force light mode for PDF generation (avoids oklch parsing issues in html2canvas)
      wasDark = await forceLightModeForPdf();
      
      // Generate the PDF from assessment data
      // const pdfBlob = await generateAssessmentPdf(
      //   assessmentData, 
      //   `essential-eight-assessment-${versionInfo.version || '1.0'}.pdf`
      // );
      const pdfBlob = await generatePdf(document.getElementById('pdf-content'), `essential-eight-assessment-${versionInfo.version || '1.0'}.pdf`, { download: false, landscape: true, returnBlob: true, scale: 0.5 });
      console.log(pdfBlob);
      
      // Convert PDF blob to base64 for API transmission
      pdfBase64 = await pdfToBase64(pdfBlob as Blob);
      console.log('[HubspotService] PDF generated successfully');
    } catch (pdfError) {
      console.error('[HubspotService] Error generating PDF for HubSpot attachment:', pdfError);
      // Continue without PDF if generation fails
    } finally {
      // Restore the previous theme mode
      restoreThemeAfterPdf(wasDark);
    }
    
    // Prepare data for HubSpot API
    try {
      const hubspotToken = sessionStorage.getItem('hubspot-token');
      const timestamp = new Date().toISOString();
      
      // Generate a unique identifier for file names
      const uniqueId = Math.random().toString(36).substring(2, 10);
      const formattedDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const assessmentType = questionnaireType === 'PRIVACY_ACT' ? 'privacy-act' : 'essential-eight';
      const assessmentTitle = questionnaireType === 'PRIVACY_ACT' ? 'Privacy Act Assessment' : 'Essential Eight Assessment';
      const filename = `${assessmentType}-assessment-${versionInfo.version || '1.0'}-${email.replace('@', '-at-')}-${formattedDate}-${uniqueId}.pdf`;
      
      // Create payload with both JSON results and PDF as separate notes
      const payload: any = {
        email,
        token: hubspotToken || '',
        notes: [
          {
            title: `${assessmentTitle} Results (${versionInfo.version || '1.0'})`,
            content: `${assessmentTitle} completed on ${new Date().toLocaleDateString()}\n\nOverall ${questionnaireType === 'PRIVACY_ACT' ? 'Compliance' : 'Maturity'} Level: ${overallMaturity}\nQuestionnaire Version: ${versionInfo.version || '1.0'}\n\nDetailed Results:\n${jsonResults}`,
            timestamp
          }
        ]
      };
      
      // Add PDF as a separate note with attachment if available
      if (pdfBase64) {
        payload.notes.push({
          title: `${assessmentTitle} PDF Report (${versionInfo.version || '1.0'})`,
          content: `PDF report generated on ${new Date().toLocaleDateString()}`,
          timestamp,
          attachments: [
            {
              name: filename,
              data: pdfBase64,
              contentType: 'application/pdf'
            }
          ]
        });
      }
      
      // Debug log to see what we're sending
      console.log('[HubspotService] Sending payload to HubSpot API:', {
        email: payload.email,
        notesCount: payload.notes?.length || 0,
        hasNotes: Array.isArray(payload.notes) && payload.notes.length > 0,
        hasPdf: !!pdfBase64
      });
      
      // IMPORTANT: The issue is here - we're stringifying the body twice!
      // deiterateAPI.POST will stringify the body again, so we need to pass the object directly
      // Send to our API endpoint that will handle HubSpot notes creation
      const response = await deiterateAPI.POST('/hubspot-update', {
        body: JSON.stringify(payload)  // Changed from JSON.stringify(payload) to payload
      }, false);
      
      // Debug the response
      console.log('[HubspotService] API response:', response);
      
      console.log('[HubspotService] Assessment results successfully sent to HubSpot', 
        pdfBase64 ? 'with PDF attachment' : 'without PDF attachment');
      
      // Clear the pending flag since we succeeded
      sessionStorage.removeItem('e8-assessment-pending-hubspot');
      
      return; // Success case
    } catch (hubspotError) {
      console.error('[HubspotService] Error sending assessment to HubSpot:', hubspotError);
      // Data is already stored in sessionStorage as backup
      
      // Display a user-friendly message
      alert('Your assessment has been saved locally. We encountered an issue sending it to our server, but you can still view and print your results.');
    }
    
  } catch (error) {
    console.error('[HubspotService] Error preparing assessment results for HubSpot:', error);
  }
};

/**
 * Get stored assessment data
 * 
 * @returns {any | null} Stored assessment data or null if none exists
 */
export const getStoredAssessmentData = (): any | null => {
  try {
    const data = sessionStorage.getItem('e8-assessment-data');
    return data ? JSON.parse(data).assessmentData : null;
  } catch (error) {
    console.error('[HubspotService] Error retrieving assessment data:', error);
    return null;
  }
};

/**
 * Clear stored assessment data
 */
export const clearStoredAssessmentData = (): void => {
  sessionStorage.removeItem('e8-assessment-data');
};

/**
 * Check if user has registered with email
 * 
 * @returns {boolean} Whether user has registered
 */
export const isUserRegistered = (questionnaireType: QuestionnaireType = 'ESSENTIAL_EIGHT'): boolean => {
  const key = questionnaireType === 'PRIVACY_ACT' ? 'privacy-act-lead-email' : 'e8-lead-email';
  return !!sessionStorage.getItem(key);
};


// ⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⢀⣀⡼⠚⠉⠉⠉⠙⢯⣢⠀⠀⠀⠀⠀⠀
// ⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⢠⢺⠊⠀⠀⣀⠀⠀⠀⠀⢱⣧⠀⠀⠀⠀⠀
// ⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⡠⣮⠃⠀⠀⠘⠛⠀⠀⠀⠀⢸⣿⠀⠀⠀⠀⠀
// ⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⢠⣿⠁⠀⠀⠀⠀⠀⠀⢀⣼⠒⣺⣻⠀⠀⠀⠀⠀
// ⠀⠀⠀⠀⠀⣀⡤⠖⠒⠛⠃⠀⠀⠀⠀⠀⠀⠀⠀⢞⠁⠈⠉⡿⡿⡄⠀⠀⠀⠀
// ⠀⠀⠀⢀⣴⡴⠊⠁⠀⠀⠀⡴⠁⠀⠀⠀⠀⠀⠀⠈⠛⠦⣄⡀⣹⣖⠀⠀⠀⠀
// ⠀⠀⢀⣾⡏⠀⠀⠀⠀⠀⠜⠁⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠈⠛⢿⣧⠀⠀⠀
// ⠀⠀⣎⠏⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠙⢦⡀⠀
// ⣠⣠⠏⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣠⡀⠀⠀⠀⠀⠀⠀⠀⣄⠀⠀⠀⠈⠳⡅
// ⢳⠁⠀⠀⠀⠀⠀⠀⠀⠀⢀⠤⠒⠈⢞⣁⣀⣀⡀⢀⡀⣀⡀⠓⠉⠒⠦⡀⠀⢱
// ⡾⠀⠀⠀⠀⠀⠀⠀⢰⠛⠉⠀⠀⠀⢰⠛⠛⠛⠁⠈⠙⠛⠛⡇⠀⠀⠀⠈⠛⡄
// ⢻⠀⠀⠀⠀⠀⠀⠀⠸⣄⣀⣀⡀⢀⡎⠀⠀⠀⠀⠀⠀⠀⠀⢳⡀⠀⣀⣀⣠⠃
// ⢸⡄⠀⠀⠀⠀⠀⠀⠀⠈⠀⠉⠉⠉⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠉⠉⠉⠈⠁⢠
// ⠸⡽⡄⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠠⡼
// ⠀⠙⠹⢦⡀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀  ⢀⡤⡞⠀
// ⠀⠀⠀⠀⠙⢲⣤⣀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣀⣠⣤⡶⠛⠉⠀⠀
// ⠀⠀⠀⠀⠀⠘⢿⣿⣿⣶⡦⠶⠶⣾⠏⠉⠉⠉⠉⠉⠉⢉⢿⣿⣷⠃⠀⠀⠀⠀
// ⠀⠀⠀⠀⠀⠀⢸⠁⡸⠷⣶⣴⣶⣦⡀⠀⠀⢀⣤⣤⣴⣵⡿⢞⠿⡶⣄⠀⠀⠀
// ⠀⠀⠀⠀⠀⠀⠻⠤⠿⠧⠶⠶⠤⢧⠃⠀⠀⠙⠾⢟⣛⣁⣒⣻⣅⣙⡾⠀⠀⠀
// IMPORTANT: During the onboarding process, the assessment results stored in HubSpot
// need to be imported into the customer's account in Firebase. This should be implemented
// as part of the onboarding flow when a prospective customer becomes an actual customer
// with a tenant ID.